import { http } from '@/utils'
import type { UserResult } from '@/types/apiTypes'
import { QrType, AgreementType } from '@/types/constantsEnum'
import type { ResLoginQr, ReqCreateOrder, ReqChangeUserInfo, PaginationData } from '@/api/type'
import type {
  ReqRegisterByAccount,
  ReqLoginByPassword,
  ReqLoginByCode,
  GeeTestForm,
  ReqCreateOrder,
  ReqUpdateUserInfo,
  ReqChangePassword,
  ReqPagination,
  ReqGetNewsList,
  ReqGetOrderHistory,
  ReqGetInviteRecord,
  ReqGetIntegralRecord,
  ReqGetRebateRecordHistory,
  ReqGetWithdrawRecordHistory,
  ReqSetPayPassword,
  ReqUpdatePayAccount,
  ReqWithdraw,
  AddPrivatePost,
  ReqPaginationByOrder,
} from '@/api/type/params'
import type {
  ResRegisterLoginUser,
  ResCommon,
  ResGetGZHQRCode,
  ResCreateOrder,
  ResGetIntegralRecord,
  ResGetContactInfo,
  ResGetNewsList,
  ResGetNewsDetail,
  ResGetOrderHistory,
  ResGetInviteRecord,
  ResGetInviteIntegralRecord,
  ResGetRebateRecordHistory,
  ResGetWithdrawRecordHistory,
  ResGetPayAccount,
  ResGetUserWallet,
  ResGetPrivatePost,
} from '@/api/type/result'

const userApi = {
  // 注册
  registerByAccount: (data?: ReqRegisterByAccount) => {
    return http.post<ReqRegisterByAccount, ResRegisterLoginUser>('user/register', { data })
  },
  // 密码登录
  loginByPassword: (data?: ReqLoginByPassword) => {
    return http.post<ReqLoginByPassword, ResRegisterLoginUser>('user/login', {
      data,
    })
  },
  // 验证码登录
  loginByCode: (data?: ReqLoginByCode) => {
    return http.post<ReqLoginByCode, ResRegisterLoginUser>('user/login/verify/code', {
      data,
    })
  },
  // 退出登录
  logout: () => {
    return http.post<void, ResCommon>('user/login/out', {})
  },
  // 发送验证码
  sendVerifyCode: (data: GeeTestForm) => {
    return http.post<GeeTestForm, ResCommon>('user/send/verify/code', {
      data,
    })
  },
  /**
   * 获取公众号二维码
   */
  getGZHQRCode: (type: QrType, params?: object) => {
    return http.get<any, ResGetGZHQRCode>(`user/wx/gz/qr/${type}`, { params })
  },
  /**
   * 检查web端微信登录状态
   */
  // checkWebWxLoginStatus: (hash: string) => {
  //   return http.get<any, ResLoginQr>(`user/check/gz/login/${hash}`)
  // },
  /**
   * 绑定账号
   */
  bindAccount: (data: ReqBindAccount) => {
    return http.post<ReqBindAccount, ResRegisterLoginUser>('user/login/after/wx', {
      data,
    })
  },
  /**
   * 创建订单
   */
  createOrder: (data?: ReqCreateOrder) => {
    return http.post<ReqCreateOrder, ResCreateOrder>('order/purchase', {
      data,
    })
  },
  /**
   * 刷新支付状态
   */
  refreshPayStatus: (id: string) => {
    return http.post<string, ResCommon>(`order/purchase-refresh/${id}`, {})
  },
  /**
   * 更改用户信息
   */
  changeUserInfo: (data?: ReqUpdateUserInfo) => {
    return http.post<ReqUpdateUserInfo, ResRegisterLoginUser>('user/update_info', {
      data,
    })
  },
  /**
   * 获取用户信息
   */
  getUserInfo: () => {
    return http.get<void, ResRegisterLoginUser>('user/info')
  },

  /**
   * 修改密码
   */
  changePassword: (data: ReqChangePassword) => {
    return http.post<ReqChangePassword, ResCommon>('user/change/password', {
      data,
    })
  },
  /**
   * 获取积分记录
   */
  getIntegralRecord: (params?: ReqGetIntegralRecord) => {
    return http.get<ReqGetIntegralRecord, ResGetIntegralRecord>('user_benefit/credit_history', {
      params,
    })
  },
  /**
   * 获取我的邀请返积分
   */
  getInviteIntegralRecord: (params?: ReqPagination) => {
    return http.get<ReqPagination, ResGetInviteIntegralRecord>(
      'user_benefit/rebate_credit_history',
      {
        params,
      },
    )
  },
  /**
   * 获取协议
   */
  getAgreement: (params: { type: AgreementType }) => {
    return http.get<any, ResCommon>(`system_config/policy`, { params })
  },

  /**
   * 获取联系方式
   */
  getContactInfo: () => {
    return http.get<any, ResGetContactInfo>(`system_config/contact_info`)
  },
  // 获取新闻列表
  getNewsList: (params: ReqGetNewsList) => {
    return http.get<ReqGetNewsList, ResGetNewsList>(`company_news`, { params })
  },
  // 获取新闻详情
  getNewsDetail: (id: string) => {
    return http.get<any, ResGetNewsDetail>(`company_news/${id}`)
  },
  /**
   * 获取套餐订单记录
   */
  getOrderHistory: (params?: ReqGetOrderHistory) => {
    return http.get<ReqGetOrderHistory, ResGetOrderHistory>('order/history', {
      params,
    })
  },
  /**
   * 获取我的邀请记录
   */
  getInviteRecord: (params?: ReqPagination) => {
    return http.get<ReqPagination, ResGetInviteRecord>('user/invitees', {
      params,
    })
  },
  /**
   * 获取邀请总人数
   */
  getInviteTotal: () => {
    return http.get<void, ResCommon>('user/invitees/count')
  },
  /**
   * 获取累计积分
   */
  getAccumulatedIntegral: () => {
    return http.get<void, ResCommon>('user_benefit/rebate_credit_summary')
  },
  /**
   * 获取累计佣金
   */
  getAccumulatedCommission: () => {
    return http.get<void, ResCommon>('user_wallet/rebate_summary')
  },
  /**
   * 获取返佣记录
   */
  getRebateRecordHistory: (params?: ReqGetRebateRecordHistory) => {
    return http.get<ReqGetRebateRecordHistory, ResGetRebateRecordHistory>(
      'user_wallet/rebate_history',
      {
        params,
      },
    )
  },
  /**
   * 获取提现记录
   */
  getWithdrawRecordHistory: (params?: ReqGetWithdrawRecordHistory) => {
    return http.get<ReqGetWithdrawRecordHistory, ResGetWithdrawRecordHistory>(
      'user_wallet/withdraw_history',
      {
        params,
      },
    )
  },
  /**
   * 获取支付账号
   */
  getPayAccount: () => {
    return http.get<void, ResGetPayAccount>('user/pay_account')
  },
  /**
   * 更新支付账号
   */
  updatePayAccount: (data: ReqUpdatePayAccount) => {
    return http.post<ReqUpdatePayAccount, ResCommon>('user/update/pay_account', {
      data,
    })
  },
  /**
   * 设置支付密码
   */
  setPayPassword: (data: ReqSetPayPassword) => {
    return http.post<ReqSetPayPassword, ResCommon>('user/update/pay_password', {
      data,
    })
  },

  /**
   * 提现
   */
  withdraw: (data: ReqWithdraw) => {
    return http.post<ReqWithdraw, ResCommon>('user_wallet/withdraw', {
      data,
    })
  },
  /**
   * 获取用户账号
   */
  getUserWallet: () => {
    return http.get<void, ResGetUserWallet>('user_wallet')
  },
  // 添加树洞
  privatePost: (data: AddPrivatePost) => {
    return http.post<AddPrivatePost, ResCommon>('private_post', {
      data,
    })
  },
  // 获取树洞列表
  getprivatePost: (params: ReqPaginationByOrder) => {
    return http.get<ReqPaginationByOrder, ResGetPrivatePost>('private_post', {
      params,
    })
  },
  // 删除树洞
  deleteprivatePost: (id: string) => {
    return http.delete<void, ResCommon>(`private_post/${id}`)
  },
  // 查询语言转文字
  getPostAudioToText: (id: string, idx: number) => {
    return http.get<void, ResCommon>(`private_post/${id}/${idx}/text`)
  },
}

export default userApi
